import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "@store/index";
import { updateDeliveryOrderGeneralApi } from "../delivery/api";
import { DeliveryDetails } from "../delivery/types";
import {
  cancelLoadingOrderApi,
  cancelLoadingOrderWhsApi,
  completeLoadingOrderApi,
  exportLoadingDdtApi,
  exportLoadingOrderApi,
  exportTmsApi,
  exportWeighingSlipApi,
  getAggregatedPackingShortApi,
  getDeliveryOrderDetailsApi,
  getPackingShortApi,
  getTmsApi,
  getTmsWhsApi,
  getWhsByIdApi,
  reviseLoadingOrderApi,
  reviseLoadingOrderWhsApi,
  saveFilterDataApi,
  updatePackingListApi,
  updateTmsApi,
  updateWhsApi,
} from "./api";
import {
  AggregatedPackingPropsResponse,
  DdtProps,
  OrderProps,
  PackingProps,
  PackingPropsResponse,
  RequestTmsParams,
  TMSProps,
  TMSResponseProp,
  TMSUpdateProps,
  TMSWhsProps,
  TMSWhsUpdateProps,
  WeighingProps,
} from "./types";

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type TmsState = {
  tms: ViewState<TMSProps[] | TMSWhsProps[] | null> & {
    total: number;
    page: number;
    size: number;
    currentParams: Partial<RequestTmsParams> | null;
  };
  tmsById: ViewState<TMSProps[] | TMSWhsProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  tmsWhs: ViewState<TMSWhsProps[] | null> & {
    total: number;
    page: number;
    size: number;
    currentParams: Partial<RequestTmsParams> | null;
  };
  delivery: ViewState<DeliveryDetails[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  packing: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  aggregated: ViewState<AggregatedPackingPropsResponse | null>;
  aggregatedInstock: ViewState<AggregatedPackingPropsResponse | null>;
  aggregatedDelivered: ViewState<AggregatedPackingPropsResponse | null>;
  exporting: {
    loading: boolean;
    error: string | null;
    success: boolean | null;
  };
  filter: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  IN_STOCK: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  DELIVERY: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
};

export type TmsKey =
  | "tms"
  | "tmsById"
  | "tmsWhs"
  | "packing"
  | "aggregated"
  | "aggregatedInstock"
  | "aggregatedDelivered"
  | "exporting"
  | "filter"
  | "delivery"
  | "IN_STOCK"
  | "DELIVERY";

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: TmsState = {
  IN_STOCK: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  DELIVERY: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  tms: {
    ...getInitialState<TMSProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
    currentParams: null,
  },
  tmsById: {
    ...getInitialState<TMSProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  tmsWhs: {
    ...getInitialState<TMSWhsProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
    currentParams: null,
  },
  packing: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  aggregated: getInitialState<AggregatedPackingPropsResponse | null>(null),
  aggregatedInstock: getInitialState<AggregatedPackingPropsResponse | null>(
    null,
  ),
  aggregatedDelivered: getInitialState<AggregatedPackingPropsResponse | null>(
    null,
  ),
  exporting: {
    loading: false,
    error: null,
    success: null,
  },
  filter: {
    loading: false,
    error: null,
    success: null,
  },
  delivery: {
    ...getInitialState<DeliveryDetails[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
};

const tms = createSlice({
  name: "tms",
  initialState: initialState,
  reducers: {
    getStart(state: TmsState, action: PayloadAction<TmsKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getTmsSuccess(
      state: TmsState,
      action: PayloadAction<TMSResponseProp & { params?: RequestTmsParams }>,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.tms.items = items;
      state.tms.total = total;
      state.tms.page = page;
      state.tms.size = size;
      state.tms.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.tms.currentParams = exportParams;
      }
    },
    getTmsByIdSuccess(state: TmsState, action: PayloadAction<TMSResponseProp>) {
      const { items, total, page, size } = action.payload;
      state.tmsById.items = items;
      state.tmsById.total = total;
      state.tmsById.page = page;
      state.tmsById.size = size;
      state.tmsById.loading = false;
    },
    getTmsWhsSuccess(
      state: TmsState,
      action: PayloadAction<{
        items: TMSWhsProps[] | null;
        total: number;
        page: number;
        size: number;
        params?: RequestTmsParams;
      }>,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.tmsWhs.items = items;
      state.tmsWhs.total = total;
      state.tmsWhs.page = page;
      state.tmsWhs.size = size;
      state.tmsWhs.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.tmsWhs.currentParams = exportParams;
      }
    },
    createSuccess(
      state: TmsState,
      action: PayloadAction<{ success: string; chain?: boolean }>,
    ) {
      const { success, chain } = action.payload;
      state.tms.success = success;
      state.tms.error = null;
      if (!chain) {
        state.tms.loading = false;
      }
    },
    createWhsSuccess(
      state: TmsState,
      action: PayloadAction<{ success: string; chain?: boolean }>,
    ) {
      const { success, chain } = action.payload;
      state.tmsWhs.success = success;
      state.tmsWhs.error = null;
      if (!chain) {
        state.tmsWhs.loading = false;
      }
    },
    getSuccess(
      state: TmsState,
      action: PayloadAction<{ key: TmsKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    updatePackingSuccess(
      state: TmsState,
      action: PayloadAction<{ success: string }>,
    ) {
      const { success } = action.payload;
      state.packing.success = success;
      state.packing.loading = false;
      state.tms.loading = false;
      state.tms.error = null;
    },
    getCommoditiesSuccess(
      state: TmsState,
      action: PayloadAction<PackingPropsResponse>,
    ) {
      const { items, total, page, size } = action.payload;
      state.packing.items = items;
      state.packing.total = total;
      state.packing.page = page;
      state.packing.size = size;
      state.packing.loading = false;
    },
    getPackingWithStatusSuccess(
      state: TmsState,
      action: PayloadAction<{
        key: "IN_STOCK" | "DELIVERY";
        data: PackingPropsResponse;
      }>,
    ) {
      const { key, data } = action.payload;
      state[key].items = data.items;
      state[key].total = data.total;
      state[key].page = data.page;
      state[key].size = data.size;
      state[key].loading = false;
    },
    getAggregatedSuccess(
      state: TmsState,
      action: PayloadAction<{
        key: "aggregated" | "aggregatedInstock" | "aggregatedDelivered";
        data: AggregatedPackingPropsResponse;
      }>,
    ) {
      const { key, data } = action.payload;
      state[key].items = data;
      state[key].loading = false;
      state[key].error = null;
    },
    setTmsList(
      state: TmsState,
      action: PayloadAction<{
        id: number;
        data: Partial<TMSProps & TMSUpdateProps>;
        params?: TMSUpdateProps;
      }>,
    ) {
      const { data, params } = action.payload;
      if (!state.tms.items) return;

      const idx = state.tms.items.findIndex((item) => item.id === data?.id);
      if (idx === -1) return;

      if (params?.loading_date !== undefined) {
        if (!(state.tms.items[idx] as TMSProps).delivery_info) {
          (state.tms.items[idx] as TMSProps).delivery_info = {} as any;
        }
        (state.tms.items[idx] as TMSProps).delivery_info.loading_date =
          params.loading_date as string;
      }

      state.tms.items[idx] = {
        ...state.tms.items[idx],
        ...data,
      } as TMSProps;
      state.tms.loading = false;
    },
    updateOrderItem(
      state: TmsState,
      action: PayloadAction<{ id: number; data: Record<string, any> }>,
    ) {
      const { id, data } = action.payload;
      const items = state.tmsById.items;
      if (!items) return;

      const idx = items.findIndex((item) => item.id === id);
      if (idx === -1) return;

      items[idx] = {
        ...items[idx],
        ...data,
      };
    },
    setDeliveryOrder(
      state: TmsState,
      action: PayloadAction<DeliveryDetails[]>,
    ) {
      state.delivery.items = action.payload;
      state.delivery.loading = false;
      state.delivery.error = null;
    },
    getFailure(
      state: TmsState,
      action: PayloadAction<{ key: TmsKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state[key].loading = false;
      state[key].error = error;
      state.exporting.loading = false;
      state.tms.loading = false;
    },
    cleanData(state: TmsState, action: PayloadAction<TmsKey>) {
      state[action.payload as "tms" | "packing"].items = null;
    },
    hideWarning(state: TmsState, action: PayloadAction<TmsKey>) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
      state.exporting.error = null;
      state.exporting.success = null;
    },
    startExport(state: TmsState) {
      state.exporting = {
        loading: true,
        error: null,
        success: null,
      };
    },
    successExport(state: TmsState) {
      state.exporting.success = true;
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getStart,
  getSuccess,
  setTmsList,
  hideWarning,
  cleanData,
  createSuccess,
  createWhsSuccess,
  getTmsSuccess,
  getTmsByIdSuccess,
  getTmsWhsSuccess,
  updateOrderItem,
  getCommoditiesSuccess,
  getAggregatedSuccess,
  getFailure,
  startExport,
  successExport,
  updatePackingSuccess,
  setDeliveryOrder,
  getPackingWithStatusSuccess,
} = tms.actions;

export default tms.reducer;

export const fetchTmsList =
  (id: number, params: RequestTmsParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tms"));
      const data = await getTmsApi(id, params);
      dispatch(getTmsSuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const fetchTmsListById =
  (id: number, params: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tmsById"));
      const data = await getTmsApi(id, params);
      dispatch(getTmsByIdSuccess(data));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const fetchWhsListById =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tmsById"));
      const data = await getWhsByIdApi(id);
      dispatch(
        getTmsByIdSuccess({ items: [data], total: 1, page: 1, size: 1 }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "tmsById", error }));
    }
  };

export const fetchTmsWhsList =
  (id: number, params: RequestTmsParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tmsWhs"));
      const data = await getTmsWhsApi(id, params);
      dispatch(getTmsWhsSuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "tmsWhs", error }));
    }
  };

export const saveFilterData =
  (sid: number, id: number, params: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("filter"));
      const data = await saveFilterDataApi(id, params);
      dispatch(updateOrderItem({ id, data }));
      dispatch(
        getSuccess({
          key: "filter",
          success: "Filter updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "filter", error }));
    }
  };

export const updateTmsList =
  ({
    id,
    params,
    shippingId,
    fetchTmsListParams,
  }: {
    id: number;
    params: Partial<TMSUpdateProps>;
    shippingId: number;
    fetchTmsListParams: any;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tms"));
      await updateTmsApi(id, params);
      dispatch(fetchTmsList(shippingId, fetchTmsListParams));
      dispatch(
        createSuccess({
          success: "TMS list updated successfully",
          chain: true,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const updateWhsList =
  ({
    id,
    params,
    shippingId,
    fetchTmsListParams,
  }: {
    id: number;
    params: Partial<TMSWhsUpdateProps>;
    shippingId: number;
    fetchTmsListParams: any;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tmsWhs"));
      await updateWhsApi(id, params);
      dispatch(fetchTmsWhsList(shippingId, fetchTmsListParams));
      dispatch(
        createWhsSuccess({
          success: "TMS (WHS) list updated successfully",
          chain: true,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsWhs", error }));
    }
  };

export const updateTmsIntro =
  ({
    id,
    params,
    callback,
    isWhs = false,
    deliveryId,
  }: {
    id: number;
    params: Partial<TMSUpdateProps> & { truck_supplier_id?: number | null };
    callback: () => void;
    isWhs?: boolean;
    deliveryId?: number;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tms"));
      const { truck_supplier_id, ...loadingParams } = params;

      if (deliveryId != null && truck_supplier_id !== undefined) {
        await updateDeliveryOrderGeneralApi(deliveryId, { truck_supplier_id });
      }

      await updateTmsApi(id, loadingParams, isWhs);
      callback();
      dispatch(
        getSuccess({
          key: "packing",
          success: "Loading order updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing", error }));
    }
  };

export const fetchPacking =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      page: number;
      size: number;
      order_by?: string;
      [key: string]: any;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("packing"));
      const data = await getPackingShortApi(id, params);
      dispatch(getCommoditiesSuccess(data));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "packing", error }));
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const fetchPackingWithStatus =
  ({
    id,
    params,
    key,
  }: {
    id: number;
    params: any;
    key: "IN_STOCK" | "DELIVERY";
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart(key));
      const data = await getPackingShortApi(id, params);
      dispatch(getPackingWithStatusSuccess({ key, data }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const fetchAggregatedPacking =
  ({
    id,
    params,
    key = "aggregated",
  }: {
    id: number;
    params: Record<string, any>;
    key?: "aggregated" | "aggregatedInstock" | "aggregatedDelivered";
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart(key));
      const data = await getAggregatedPackingShortApi(id, params);
      dispatch(getAggregatedSuccess({ key, data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key, error }));
    }
  };

export const updatePackingList =
  ({
    id,
    params,
    next,
  }: {
    id: number;
    params: {
      packing_list_ids: number[];
      mark?: string;
    };
    next?: {
      id: number;
      orderId: number;
      scId: number;
      params: any;
    };
  }): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const data = await updatePackingListApi(id, params);
      if (next) {
        dispatch(
          fetchTmsListById(next.scId, {
            id: next.orderId,
          }),
        );
      } else dispatch(setTmsList({ id, data }));
      dispatch(
        updatePackingSuccess({
          success: "Loading updated successfully",
        }),
      );
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing", error }));
      return false;
    }
  };

export const updatePackingListWhs =
  ({
    id,
    params,
    next,
  }: {
    id: number;
    params: {
      packing_list_ids: number[];
      mark?: string;
    };
    next?: {
      orderId: number;
    };
  }): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const data = await updatePackingListApi(id, params, true);
      if (next) {
        dispatch(fetchWhsListById(next.orderId));
      } else dispatch(setTmsList({ id, data }));
      dispatch(
        updatePackingSuccess({
          success: "Loading updated successfully",
        }),
      );
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing", error }));
      return false;
    }
  };

export const cancelLoadingOrder =
  (id: number, sid: number, params: Record<string, any>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tms"));
      await cancelLoadingOrderApi(id);
      dispatch(fetchTmsList(sid, params));
      dispatch(
        createSuccess({
          success: "Loading order canceled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const cancelLoadingOrderWhs =
  (id: number, sid: number, params: Record<string, any>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("tmsWhs"));
      await cancelLoadingOrderWhsApi(id);
      dispatch(fetchTmsWhsList(sid, params));
      dispatch(
        createWhsSuccess({
          success: "Loading order canceled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsWhs", error }));
    }
  };

export const completeLoadingOrder =
  (id: number, sid: number, isWhs?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      await completeLoadingOrderApi(id, isWhs);
      if (isWhs) {
        dispatch(fetchWhsListById(id));
      } else
        dispatch(
          fetchTmsListById(sid, {
            id,
          }),
        );
      dispatch(
        updatePackingSuccess({
          success: "Loading completed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsById", error }));
    }
  };

export const reviseLoadingOrder =
  (
    id: number,
    sid: number,
    data: { packing_list_ids: number[] },
  ): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      await reviseLoadingOrderApi(id, data);
      dispatch(
        fetchTmsListById(sid, {
          id,
        }),
      );
      dispatch(
        updatePackingSuccess({
          success: "Loading revised successfully",
        }),
      );
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsById", error }));
      return false;
    }
  };

export const reviseLoadingOrderWhs =
  (
    id: number,
    sid: number,
    data: { packing_list_ids: number[] },
  ): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      await reviseLoadingOrderWhsApi(id, data);
      dispatch(fetchWhsListById(id));
      dispatch(
        updatePackingSuccess({
          success: "Loading revised successfully",
        }),
      );
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsById", error }));
      return false;
    }
  };

export const fetchOrderDetails =
  (id: number, truck_id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("delivery"));
      const { items } = await getDeliveryOrderDetailsApi(id, truck_id);
      dispatch(setDeliveryOrder(items));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tmsById", error }));
    }
  };

export const exportLoadingOrder =
  (id: number, params: OrderProps, isWhs = false): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportLoadingOrderApi(id, params, isWhs);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const exportLoadingDdt =
  (
    id: number,
    scId: number,
    param: DdtProps,
    params: Record<string, any>,
    navigate: boolean,
    status: string,
    isWhs = false,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportLoadingDdtApi(id, param, isWhs);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (!params?.ddt_number && !navigate)
        if (isWhs) dispatch(fetchTmsWhsList(scId, params));
        else dispatch(fetchTmsList(scId, { ...params }));
      if (navigate && status !== "EXECUTED") {
        if (isWhs) dispatch(fetchWhsListById(id));
        else
          dispatch(
            fetchTmsListById(scId, {
              id,
            }),
          );
      }
      dispatch(successExport());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const exportWeighingSlip =
  (id: number, params: WeighingProps, isWhs = false): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportWeighingSlipApi(id, params, isWhs);
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };

export const exportTms =
  (id: number, params?: Partial<RequestTmsParams>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportTmsApi(id, params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "tms", error }));
    }
  };
